import { PrimitiveType } from "@buildwithflux/models";
import { vec2 } from "../../../common";
import { geom2 } from "../../../common/geometry/geom2";
import { mat3 } from "../../../common/geometry/mat3";
import { pointDistance } from "../queries/distance";
import { approxFittingPolygon } from "./approxFittingPolygon";
import { isConvex } from "./isConvex";
import { chainCompoundPrimitiveTransforms } from "./utils";
/**
 * @remarks Do not rely on this function to compute accurate distance between shapes if one of the shapes
 * is a "difference primitive". See notes in implementation.
 *
 * @privateRemarks As of writing, this function is only used for the measurement helper.
 */
export function shortestSegmentBetweenPrimitives(primitiveA, primitiveB, segments) {
    //
    // Before actually using the brute force polygon-polygon distance we test for special easier cases, which will be common
    const transformDecomposedA = mat3.decompose(primitiveA.transform);
    const transformDecomposedB = mat3.decompose(primitiveB.transform);
    const matrixIsSimpleA = mat3.affineSanityCheck(primitiveA.transform) &&
        transformDecomposedA.scaleIsEqual &&
        transformDecomposedA.xyDot === 0;
    const matrixIsSimpleB = mat3.affineSanityCheck(primitiveB.transform) &&
        transformDecomposedB.scaleIsEqual &&
        transformDecomposedB.xyDot === 0;
    // Easier case: equal scaling circle to circle
    if (primitiveA.type === PrimitiveType.Circle &&
        primitiveB.type === PrimitiveType.Circle &&
        matrixIsSimpleA &&
        matrixIsSimpleB) {
        const result = specialCaseCircleCircle(primitiveA, primitiveB, transformDecomposedA, transformDecomposedB);
        if (!result.pointA || !result.pointB || !result.minA || !result.minB || !result.maxA || !result.maxB) {
            return null; // This should not happen
        }
        else {
            return {
                minA: result.minA,
                minB: result.minB,
                maxA: result.maxA,
                maxB: result.maxB,
                pointA: result.pointA,
                pointB: result.pointB,
                minDist: result.minDist,
            };
        }
    }
    // Easier case: rectangle to circle
    if (primitiveA.type === PrimitiveType.Rectangle && primitiveB.type === PrimitiveType.Circle && matrixIsSimpleB) {
        return specialCaseRectangleCircle(primitiveA, primitiveB, segments, transformDecomposedB);
    }
    if (primitiveB.type === PrimitiveType.Rectangle && primitiveA.type === PrimitiveType.Circle && matrixIsSimpleA) {
        return specialCaseRectangleCircle(primitiveB, primitiveA, segments, transformDecomposedA);
    }
    // TODO: Other special cases, such as oblongs, lines and rounded rectangles
    // Now more and more primitive support the closestPoint parameter in the pointDistance function, so refactor soon
    // 1. Recurses over compound primitives
    if (primitiveA.type === PrimitiveType.Union || primitiveA.type === PrimitiveType.Difference) {
        const [transformedA, transformedB] = chainCompoundPrimitiveTransforms(primitiveA);
        const resultA = shortestSegmentBetweenPrimitives(transformedA, primitiveB, segments);
        const resultB = shortestSegmentBetweenPrimitives(transformedB, primitiveB, segments);
        if (!resultA || !resultB)
            return null;
        if (primitiveA.type === PrimitiveType.Union) {
            if (resultA?.minDist < resultB?.minDist)
                return resultA;
            else
                return resultB;
        }
        else if (primitiveA.type === PrimitiveType.Difference) {
            // TODO: THIS IS PROBABLY WRONG!
            if (resultA?.minDist > -resultB?.minDist)
                return resultA;
            else
                return resultB;
        }
    }
    if (primitiveB.type === PrimitiveType.Union || primitiveB.type === PrimitiveType.Difference) {
        const [transformedA, transformedB] = chainCompoundPrimitiveTransforms(primitiveB);
        const resultA = shortestSegmentBetweenPrimitives(transformedA, primitiveA, segments);
        const resultB = shortestSegmentBetweenPrimitives(transformedB, primitiveA, segments);
        if (!resultA || !resultB)
            return null;
        if (primitiveB.type === PrimitiveType.Union) {
            if (resultA?.minDist < resultB?.minDist)
                return resultA;
            else
                return resultB;
        }
        else if (primitiveB.type === PrimitiveType.Difference) {
            // TODO: THIS IS PROBABLY WRONG!
            if (resultA?.minDist > -resultB?.minDist)
                return resultA;
            else
                return resultB;
        }
    }
    // 2. Non-convex primitives are much harder to compute (TODO)
    if (!isConvex(primitiveA) || !isConvex(primitiveB)) {
        // Not supported yet!
        return null;
    }
    // 3. Approximate the two primitives into polygons
    const polygonA = approxFittingPolygon(primitiveA, segments);
    const polygonB = approxFittingPolygon(primitiveB, segments);
    // 4. Compute the min distance between the polygons
    return geom2.convexPolygonDistance(polygonA, polygonB);
}
function specialCaseCircleCircle(primitiveA, primitiveB, transformDecomposedA, transformDecomposedB) {
    const { dist: distAB, outPoint: pointA } = pointDistance(primitiveA, transformDecomposedB.translation.x, transformDecomposedB.translation.y);
    const { outPoint: pointB } = pointDistance(primitiveB, transformDecomposedA.translation.x, transformDecomposedA.translation.y);
    const minDist = distAB - primitiveB.radius * Math.abs(transformDecomposedB.scale.x);
    return { minDist, pointA, pointB, minA: pointA, minB: pointB, maxA: pointA, maxB: pointB };
}
function specialCaseRectangleCircle(primitiveA, primitiveB, segments, transformDecomposedB) {
    const polygonA = approxFittingPolygon(primitiveA, segments);
    const centerB = { x: transformDecomposedB.translation.x, y: transformDecomposedB.translation.y };
    const radiusB = primitiveB.radius * Math.abs(transformDecomposedB.scale.x);
    const distFromCircleCenter = geom2.polygonPointDistance(centerB, polygonA);
    const pointA = distFromCircleCenter.outPoint;
    const minDist = distFromCircleCenter.dist - radiusB;
    const pointB = vec2
        .chain(pointA)
        .sub(centerB)
        .normalize()
        ?.scale(primitiveB.radius)
        .applyMatrix3(primitiveB.transform)
        .xy() ?? { x: 0, y: 0 };
    return { minDist, pointA, pointB, minA: pointA, minB: pointB, maxA: pointA, maxB: pointB };
}
//# sourceMappingURL=shortestSegmentBetweenShapes.js.map